import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button, WContainer, WishlistButton } from '@components/atoms'
import styles from './PDPBar.module.sass'
import useTrans from '@hooks/useTrans'
import { ProdottoNode } from '@gql/graphql'
import { priceNotation } from '@utils/safe'
import { CartContext } from '@utils/cartContext'
import { isEcommerce } from '@utils/index'
import { useRouter } from 'next/router'
import { Check } from '@components/icons'
import { handleAddToCartTracking } from '@utils/tracking'

interface Props {
  prodotto: ProdottoNode
  className?: string
  prezzi?: { key: string; label: string; value: number }[]
  visible?: boolean
}

const PDPBar = (props: Props) => {
  const { prodotto = null, className = '', prezzi = null, visible = false } = props
  const { addToCart: contextAddToCart, cart } = useContext(CartContext)

  const t = useTrans()
  const router = useRouter()
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [loadingAddToCart, setLoadingAddToCart] = useState(false)

  const feedbackTimeout = useRef<ReturnType<typeof setTimeout>>()

  const ecommerce = isEcommerce(router.locale)

  const addToCart = async () => {
    if (feedbackTimeout.current) clearTimeout(feedbackTimeout.current)
    setShowSuccess(false)
    setShowError(false)
    setLoadingAddToCart(true)

    const res = await contextAddToCart(prodotto.pk, 1)

    setLoadingAddToCart(false)

    if (res?.success) {
      setShowSuccess(true)
      handleAddToCartTracking(prodotto, cart, 'pdp')
    } else {
      setErrorMessage(res?.message || '')
      setShowError(true)
    }

    feedbackTimeout.current = setTimeout(() => {
      setShowSuccess(false)
      setShowError(false)
    }, 3000)
  }

  useEffect(() => {
    return () => {
      if (feedbackTimeout.current) clearTimeout(feedbackTimeout.current)
    }
  }, [])

  return prodotto ? (
    <div
      className={`${styles.root} ${className} ${showSuccess ? styles['root--success'] : ''} ${
        showError ? styles['root--error'] : ''
      } ${visible ? styles[`root--visible`] : ''}`}
    >
      <WContainer>
        <div className={styles.main}>
          {!!prodotto.nome && <p className={styles.title}>{prodotto.nome}</p>}
          <div className={styles.infos}>
            {!!prezzi?.length && (
              <div className={styles.prices}>
                {prezzi.map((p) => (
                  <span key={p.key} className={`${styles.price} ${styles[`price--${p.key}`]}`}>
                    {!!p.label && <span className={styles.priceLabel}>{p.label} </span>}
                    <span className={styles.priceValue}>
                      {priceNotation(p.value, false, prodotto.valuta?.simbolo)}
                    </span>
                  </span>
                ))}
              </div>
            )}
            <div className={styles.ctaBox}>
              {ecommerce && (
                <WishlistButton prodotto={prodotto} className={styles.wishlistMobile} />
              )}
              <Button
                label={t('Aggiungi al carrello')}
                onClick={addToCart}
                className={styles.cta}
                loading={loadingAddToCart}
              />
              {ecommerce && (
                <WishlistButton prodotto={prodotto} className={styles.wishlistDesktop} />
              )}
            </div>
          </div>
        </div>
      </WContainer>
      <div className={styles.success}>
        <span className={styles.successIcon}>
          <Check />
        </span>
        <span className={styles.successLabel}>{t('Prodotto aggiunto al carrello')}</span>
      </div>
      <div className={styles.error}>
        <span className={styles.errorLabel}>
          {errorMessage || t('Non è stato possibile aggiungere il prodotto al carrello')}
        </span>
      </div>
    </div>
  ) : (
    <></>
  )
}

export default PDPBar
